import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { useQueryClient } from "@tanstack/react-query";
import FetchClients, { ClientOutput } from "../client";
import { BackpackServiceError } from "../errors";

export const SPACE_NOTES_QUERY_KEY = "space-notes";
export const NOTE_MAX_UTF8_BYTES = 10_000;
export const NOTE_TOO_LONG_MESSAGE =
  "Note cannot exceeded 10,000 characters in length";

export type SpaceNote = ClientOutput["tenantsAndSpaces"]["NoteWithUserInfo"];

const notes = {
  useQuery,
  mutations: { usePost, usePatch, useDelete },
} as const;
export default notes;

type SpaceNotesPath = {
  siteId: number;
  buildingId: number;
  spaceId: number;
};

function getSpaceNotesQueryKey({
  siteId,
  buildingId,
  spaceId,
}: SpaceNotesPath) {
  return [SPACE_NOTES_QUERY_KEY, siteId, buildingId, spaceId] as const;
}

function assertNoteBodyLength(body: string) {
  if (new TextEncoder().encode(body).length > NOTE_MAX_UTF8_BYTES) {
    throw new BackpackServiceError({
      error: { code: 1001, message: NOTE_TOO_LONG_MESSAGE },
    });
  }
}

async function getNotes(
  { siteId, buildingId, spaceId }: SpaceNotesPath,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.tenantsAndSpaces.GET(
    "/{site_id}/buildings/{building_id}/spaces/{space_id}/notes",
    {
      params: {
        path: {
          site_id: siteId,
          building_id: buildingId,
          space_id: spaceId,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );

  if (data) return data;
  throw error;
}

function useQuery({
  siteId,
  buildingId,
  spaceId,
  enabled = true,
}: SpaceNotesPath & { enabled?: boolean }) {
  return useQueryWithAccessCheckLog(
    ["Property", "read"],
    (checkFeatureAccessHeader) =>
      getNotes({ siteId, buildingId, spaceId }, checkFeatureAccessHeader),
    {
      queryKey: getSpaceNotesQueryKey({ siteId, buildingId, spaceId }),
      enabled,
      retry: false,
    }
  );
}

async function addNote(
  path: SpaceNotesPath,
  note: string,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  assertNoteBodyLength(note);
  const { data, error } = await FetchClients.tenantsAndSpaces.POST(
    "/{site_id}/buildings/{building_id}/spaces/{space_id}/note",
    {
      params: {
        path: {
          site_id: path.siteId,
          building_id: path.buildingId,
          space_id: path.spaceId,
        },
      },
      body: { body: note },
      headers: checkFeatureAccessHeader,
    }
  );

  if (data) return data;
  throw error;
}

function usePost(path: SpaceNotesPath) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Property", "edit"],
    (note: string, checkFeatureAccessHeader) =>
      addNote(path, note, checkFeatureAccessHeader),
    {
      onSettled: () =>
        queryClient.invalidateQueries({
          queryKey: getSpaceNotesQueryKey(path),
        }),
    }
  );
}

async function patchNote(
  path: SpaceNotesPath,
  noteId: number,
  note: string,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  assertNoteBodyLength(note);
  const { data, error } = await FetchClients.tenantsAndSpaces.PATCH(
    "/{site_id}/buildings/{building_id}/spaces/{space_id}/note/{note_id}",
    {
      params: {
        path: {
          site_id: path.siteId,
          building_id: path.buildingId,
          space_id: path.spaceId,
          note_id: noteId,
        },
      },
      body: { body: note },
      headers: checkFeatureAccessHeader,
    }
  );

  if (data) return data;
  throw error;
}

function usePatch(path: SpaceNotesPath) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Property", "edit"],
    (
      { noteId, note }: { noteId: number; note: string },
      checkFeatureAccessHeader
    ) => patchNote(path, noteId, note, checkFeatureAccessHeader),
    {
      onSettled: () =>
        queryClient.invalidateQueries({
          queryKey: getSpaceNotesQueryKey(path),
        }),
    }
  );
}

async function deleteNote(
  path: SpaceNotesPath,
  noteId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { error } = await FetchClients.tenantsAndSpaces.DELETE(
    "/{site_id}/buildings/{building_id}/spaces/{space_id}/note/{note_id}",
    {
      params: {
        path: {
          site_id: path.siteId,
          building_id: path.buildingId,
          space_id: path.spaceId,
          note_id: noteId,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );
  if (error) throw error;
}

function useDelete(path: SpaceNotesPath) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Property", "edit"],
    (noteId: number, checkFeatureAccessHeader) =>
      deleteNote(path, noteId, checkFeatureAccessHeader),
    {
      onSettled: () =>
        queryClient.invalidateQueries({
          queryKey: getSpaceNotesQueryKey(path),
        }),
    }
  );
}
